export interface ScrollState {
  y: number
  progress: number
  velocity: number
  direction: 1 | -1
}

type Listener = (state: ScrollState) => void

const state: ScrollState = { y: 0, progress: 0, velocity: 0, direction: 1 }
const listeners = new Set<Listener>()

let maxScroll = 0
let frame: number | null = null
let running = false
let lastY = 0
let smoothedVelocity = 0
let observer: ResizeObserver | null = null

const measure = (): void => {
  maxScroll = Math.max(document.documentElement.scrollHeight - window.innerHeight, 0)
}

const publish = (): void => {
  const root = document.documentElement
  root.style.setProperty('--scroll-y', state.y.toFixed(2))
  root.style.setProperty('--scroll-progress', state.progress.toFixed(5))
  root.style.setProperty('--scroll-velocity', state.velocity.toFixed(4))
  for (const listener of listeners) listener(state)
}

const tick = (): void => {
  frame = null
  const y = window.scrollY
  const delta = y - lastY
  lastY = y

  smoothedVelocity += (delta - smoothedVelocity) * 0.2
  if (Math.abs(smoothedVelocity) < 0.01) smoothedVelocity = 0

  state.y = y
  state.progress = maxScroll > 0 ? Math.min(Math.max(y / maxScroll, 0), 1) : 0
  state.velocity = Math.max(Math.min(smoothedVelocity / 40, 1), -1)
  if (delta !== 0) state.direction = delta > 0 ? 1 : -1

  publish()

  if (smoothedVelocity !== 0) schedule()
}

const schedule = (): void => {
  if (frame !== null) return
  frame = requestAnimationFrame(tick)
}

const onResize = (): void => {
  measure()
  schedule()
}

const start = (): void => {
  if (running) return
  running = true
  lastY = window.scrollY
  measure()
  window.addEventListener('scroll', schedule, { passive: true })
  window.addEventListener('resize', onResize, { passive: true })
  if (typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(onResize)
    observer.observe(document.documentElement)
  }
  schedule()
}

const stop = (): void => {
  running = false
  window.removeEventListener('scroll', schedule)
  window.removeEventListener('resize', onResize)
  observer?.disconnect()
  observer = null
  if (frame !== null) {
    cancelAnimationFrame(frame)
    frame = null
  }
}

export const subscribeToScroll = (listener: Listener): (() => void) => {
  listeners.add(listener)
  if (listeners.size === 1) start()
  listener(state)

  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) stop()
  }
}

export const getScrollState = (): ScrollState => state

export const remeasureScroll = measure
